import { APP_PART_TYPES } from "@lhd/features/Apps/AppForm/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { AppSelectionList } from "@lhd/types";
import { Rule } from "antd/lib/form";
import {
  APP_TYPES,
  COMPANY_POLICIES_EXTEND_OPTIONS,
  SEPARATE,
} from "./constants";
import { isBlankOrNullStatus, parseJson } from "@lhd/utility";
import { matchPath, useMatch } from "react-router-dom";
import { ROUTES } from "@lhd/constants";

export const isStatusField = (item?: AppPartType) => {
  if (!item) return false;
  const name = item.name?.trim()?.toLowerCase();
  const fieldCode = item.field_code?.trim()?.toLowerCase();
  const labelText = item.label_text?.trim()?.toLowerCase();
  return (
    name === "ステータス" ||
    name === "status" ||
    fieldCode === "ステータス" ||
    fieldCode === "status" ||
    fieldCode === "status_slicer" ||
    fieldCode === "status_filter" ||
    labelText === "ステータス" ||
    labelText === "status"
  );
};

export const isCategoryField = (item?: AppPartType) => {
  if (!item) return false;
  const name = item.name?.trim()?.toLowerCase();
  const fieldCode = item.field_code?.trim()?.toLowerCase();
  const labelText = item.label_text?.trim()?.toLowerCase();
  return (
    name === "カテゴリー" ||
    name === "カテゴリ" ||
    name === "category" ||
    fieldCode === "カテゴリー" ||
    fieldCode === "カテゴリ" ||
    fieldCode === "category" ||
    labelText === "カテゴリー" ||
    labelText === "カテゴリ" ||
    labelText === "category"
  );
};

export const isCategoryOrStatusField = (item?: AppPartType) => {
  return isCategoryField(item) || isStatusField(item);
};

export const isBusinessManualsPath = (pathname?: string): boolean => {
  const currentPath =
    pathname || (typeof window !== "undefined" ? window.location.pathname : "");
  if (!currentPath) return false;
  return Boolean(
    matchPath({ path: `/${ROUTES.BUSINESS_MANUALS}/*` }, currentPath),
  );
};

export const useIsBusinessManualsRoute = (): boolean => {
  return Boolean(useMatch(`/${ROUTES.BUSINESS_MANUALS}/*`));
};

export const isBusinessManualsApp = (
  appType?: string,
  pathname?: string,
): boolean => {
  if (appType === APP_TYPES.BUSINESS_MANUALS) return true;
  if (!appType) {
    return isBusinessManualsPath(pathname);
  }
  return false;
};

export interface IAppPartOption {
  value: string | number;
  label: string;
  default_checked?: boolean;
  disabled?: boolean;
}

export const getOptionsFormAppPart = (
  item?: AppPartType,
  appType?: string,
): IAppPartOption[] => {
  let options: IAppPartOption[] = [];

  const configData =
    typeof item?.item_config_data === "string"
      ? parseJson(item.item_config_data, {})
      : item?.item_config_data;

  const isBusinessManualsCatalogField =
    (isBusinessManualsApp(appType) ||
      item?.field_code === "status_slicer" ||
      item?.field_code === "status_filter") &&
    item?.parent_id == null &&
    (isCategoryField(item) || isStatusField(item));

  if (appType === APP_TYPES.COMPANY_POLICIES) {
    const extendOptions =
      COMPANY_POLICIES_EXTEND_OPTIONS.find(
        (x) =>
          x.name === item?.name ||
          x.name === item?.label_text ||
          x.name === item?.field_code,
      )?.options || [];

    const policyOptions =
      configData?.options?.map((option: any) => {
        return {
          value: option.value !== undefined ? option.value : option.text,
          label: option.text,
          default_checked: option.default_checked,
        };
      }) || [];

    options = [...policyOptions, ...extendOptions];
  } else if (isBusinessManualsCatalogField) {
    options =
      configData?.options?.map((option: any) => {
        const val =
          option.value !== undefined && option.value !== null
            ? Number.isInteger(Number(option.value))
              ? Number(option.value)
              : option.value
            : option.text;
        return {
          value: val,
          label: option.text,
          default_checked: option.default_checked,
        };
      }) || [];
  } else {
    options =
      configData?.options?.map((option: any) => {
        return {
          value: option.text,
          label: option.text,
          default_checked: option.default_checked,
        };
      }) || [];
  }

  // Filter out blank or __NULL__ values for status and category fields
  if (isCategoryOrStatusField(item)) {
    options = options.filter((opt) => !isBlankOrNullStatus(opt));
  }

  return options;
};

export const getRuleValidation = (item: AppPartType): Rule[] => {
  const { max_num_char, min_num_char, is_required_field } = item || {};
  const rules: Rule[] = [];

  if (is_required_field) {
    rules.push({
      required: true,
      message: `${item.label_text}は必須項目です。`,
    });
  }

  if (typeof min_num_char === "number") {
    rules.push({
      min: min_num_char,
      message: `少なくとも${min_num_char}文字を入力してください。`,
    });
  }

  if (typeof max_num_char === "number") {
    rules.push({
      max: max_num_char,
      message: `${max_num_char}文字以内で入力してください。`,
    });
  }

  return rules;
};

export const getTextSection = (
  values: number | number[] | string | string[],
  appPart: AppPartType,
  selectionDataList?: AppSelectionList,
  { omitUnknown = false }: { omitUnknown?: boolean } = {},
) => {
  if (!selectionDataList) return "";

  let arrayValues: any[] = [];
  if (Array.isArray(values)) {
    arrayValues = values;
  } else if (values !== undefined && values !== null && values !== "") {
    if (typeof values === "string" && values.startsWith("[")) {
      arrayValues = parseJson(values, []);
    } else {
      arrayValues = [values];
    }
  }

  const options =
    selectionDataList[
      appPart.app_part_type as keyof typeof selectionDataList
    ] || [];

  if (options.length === 0) return "";

  try {
    switch (appPart.app_part_type) {
      case APP_PART_TYPES.WORKER_SELECTION:
      case APP_PART_TYPES.REPRESENTATIVE_SELECTION: {
        const workerMap = new Map(
          (options || []).map((item: any) => [
            item.id,
            item.first_name && item.last_name
              ? `${item.first_name} ${item.last_name}`
              : item.full_name || item.name,
          ]),
        ) as any;
        return (
          arrayValues
            ?.map(
              (id) =>
                workerMap.get(Number(id)) ||
                workerMap.get(String(id)) ||
                (omitUnknown ? "" : id),
            )
            .filter((label) => !omitUnknown || label !== "")
            .join(SEPARATE) || ""
        );
      }
      case APP_PART_TYPES.ORGANIZATION_SELECTION:
      case APP_PART_TYPES.DEPARTMENT_SELECTION:
      case APP_PART_TYPES.DIVISION_SELECTION:
      case APP_PART_TYPES.BRANCH_SELECTION:
      case APP_PART_TYPES.COMPANY_LOCATION_SELECTION:
      case APP_PART_TYPES.COMPANY_PHONE_NUMBER_SELECTION:
      case APP_PART_TYPES.POSITION_SELECTION:
      case APP_PART_TYPES.CUSTOMER_SELECTION: {
        const organizationMap = new Map(
          (options || []).map((item) => [item.id, item.name]),
        ) as any;
        return arrayValues
          .map(
            (id) =>
              organizationMap.get(Number(id)) ||
              organizationMap.get(String(id)) ||
              (omitUnknown ? "" : id),
          )
          .filter((label) => !omitUnknown || label !== "")
          .join(SEPARATE);
      }
    }
  } catch (error) {
    console.log("error", error);
  }
  return "";
};
